import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  ButtonGroup,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import CategoryIcon from '@mui/icons-material/Category';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { accountingApi } from '../../../config/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../shared/StatsCard';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import InventoryStyleListTable from './components/InventoryStyleListTable';
import { useTranslation } from 'react-i18next';
import {
  ProductRecipeMenuService,
  ProductRecipeListItem,
  ProductListSummary,
} from './services/productRecipeMenuService';
import { RecipeModal } from './components/RecipeModal';
import { InventoryItem } from './types/inventory.types';

/**
 * Basit Üretim Reçeteleri listesi.
 *
 * Reçeteler bugüne kadar yalnızca stok kartının satır menüsünden tek tek
 * açılabiliyordu; sistemde tanımlı reçetelerin tamamını görecek bir yer yoktu.
 * Düzen ve tablo kabuğu stok yönetimi ekranıyla aynıdır (özet kartları, arama
 * satırı, `InventoryStyleListTable`); satırdan mevcut `RecipeModal` açılır,
 * böylece düzenleme/silme mantığı tek yerde kalır.
 */
const ProductRecipes: React.FC = () => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userDetails, warehouses, categories, branches } = useAuth();
  const companyId = userDetails?.companyId || null;

  // Maliyet şubesi: null = şirket geneli (ürün kartı alış fiyatı).
  const [costBranch, setCostBranch] = useState<{ id: string; name: string } | null>(null);
  const costBranchLabel = costBranch?.name || t('stock.productRecipesPage.branch.companyWide');

  const [recipes, setRecipes] = useState<ProductRecipeListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  const [summary, setSummary] = useState<ProductListSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortColumn, setSortColumn] = useState<string>('recipeName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Reçete modalı — satırdan açılır, ürünün güncel kartı API'den çekilir.
  const [modalItem, setModalItem] = useState<InventoryItem | null>(null);
  const [openingRecipeId, setOpeningRecipeId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, sortColumn, sortDirection, costBranch]);

  const fetchRecipes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await ProductRecipeMenuService.getPagedRecipes({
        page: page + 1,
        pageSize: rowsPerPage,
        search: debouncedSearchTerm,
        isActive: statusFilter === 'all' ? null : statusFilter === 'active',
        sortColumn,
        sortDirection,
        branchId: costBranch?.id,
      });
      setRecipes(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      console.error('❌ [RECIPES] Reçeteler yüklenirken hata:', err);
      setError(err?.response?.data?.message || err?.message || t('stock.productRecipesPage.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearchTerm, statusFilter, sortColumn, sortDirection, costBranch, t]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      setSummary(await ProductRecipeMenuService.getRecipeSummary());
    } catch (err) {
      // Özet kartları bilgilendirme amaçlı; hatası listeyi engellemesin.
      console.error('❌ [RECIPES] Özet alınamadı:', err);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleRefresh = () => {
    fetchRecipes();
    fetchSummary();
  };

  const formatDate = (value?: string | null): string => {
    if (!value) return '-';
    return new Date(value).toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatQuantity = (value: number): string =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(value);

  const formatCurrency = (value: number, fractionDigits = 2): string =>
    `${new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: fractionDigits,
    }).format(value)} ₺`;

  /**
   * Reçete modalı ürünün tam stok kartını bekliyor; liste satırında yalnızca
   * ad/kod var. Bu yüzden modal açılmadan önce ürün API'den çekilir.
   */
  const handleOpenRecipe = async (row: ProductRecipeListItem) => {
    setOpeningRecipeId(row.id);
    try {
      const response = await accountingApi.get(`/api/Inventory/items/${row.inventoryItemId}`);
      setModalItem(response.data as InventoryItem);
    } catch (err) {
      console.error('❌ [RECIPES] Ürün kartı çekilemedi:', err);
      setError(t('stock.productRecipesPage.errors.itemLoadFailed'));
    } finally {
      setOpeningRecipeId(null);
    }
  };

  const handleExportToExcel = () => {
    const exportData = recipes.map((r) => ({
      [t('stock.productRecipesPage.columns.recipeName')]: r.recipeName,
      [t('stock.productRecipesPage.columns.recipeCode')]: r.recipeCode || '',
      [t('stock.productRecipesPage.columns.product')]: r.inventoryItemName || '',
      [t('stock.productRecipesPage.columns.stockCode')]: r.inventoryItemStockCode || '',
      [t('stock.productRecipesPage.columns.output')]: `${formatQuantity(r.outputQuantity)} ${r.outputUnit}`,
      [t('stock.productRecipesPage.columns.ingredientCount')]: r.ingredientCount,
      [t('stock.productRecipesPage.branch.label')]: costBranchLabel,
      [t('stock.productRecipesPage.columns.totalCost')]: r.totalCost,
      [t('stock.productRecipesPage.columns.unitCost')]: r.unitCost,
      [t('stock.productRecipesPage.columns.status')]: r.isActive
        ? t('stock.productRecipesPage.status.active')
        : t('stock.productRecipesPage.status.inactive'),
      [t('stock.productRecipesPage.columns.createdAt')]: formatDate(r.createdAt),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('stock.productRecipesPage.title'));
    XLSX.writeFile(wb, `product_recipes_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const columns = useMemo<DataTableColumn<ProductRecipeListItem>[]>(() => [
    {
      id: 'recipeName',
      label: t('stock.productRecipesPage.columns.recipeName'),
      field: 'recipeName',
      sortable: true,
      minWidth: '200px',
      render: (row) => (
        <>
          <Typography variant="body2" fontWeight="medium">{row.recipeName}</Typography>
          {row.recipeCode && (
            <Typography variant="caption" color="text.secondary">{row.recipeCode}</Typography>
          )}
        </>
      ),
    },
    {
      id: 'itemName',
      label: t('stock.productRecipesPage.columns.product'),
      field: 'inventoryItemName',
      sortable: true,
      minWidth: '200px',
      render: (row) => (
        <>
          <Typography variant="body2">{row.inventoryItemName || '-'}</Typography>
          {row.inventoryItemStockCode && (
            <Typography variant="caption" color="text.secondary">{row.inventoryItemStockCode}</Typography>
          )}
        </>
      ),
    },
    {
      id: 'output',
      label: t('stock.productRecipesPage.columns.output'),
      align: 'right',
      width: '120px',
      render: (row) => (
        <Typography variant="body2">
          {formatQuantity(row.outputQuantity)} {row.outputUnit}
        </Typography>
      ),
    },
    {
      id: 'ingredientCount',
      label: t('stock.productRecipesPage.columns.ingredientCount'),
      align: 'center',
      width: '110px',
      render: (row) => (
        <Chip
          size="small"
          label={row.ingredientCount}
          color={row.ingredientCount > 0 ? 'primary' : 'default'}
          variant={row.ingredientCount > 0 ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      id: 'cost',
      label: costBranch
        ? t('stock.productRecipesPage.columns.costForBranch', { branch: costBranch.name })
        : t('stock.productRecipesPage.columns.cost'),
      align: 'right',
      minWidth: '150px',
      render: (row) => {
        if (row.ingredientCount === 0) {
          return (
            <Tooltip title={t('stock.productRecipesPage.cost.noIngredients')}>
              <Typography variant="body2" color="text.disabled">-</Typography>
            </Tooltip>
          );
        }
        return (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
              {row.currencyWarning && (
                <Tooltip title={t('stock.productRecipesPage.cost.currencyWarning')}>
                  <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                </Tooltip>
              )}
              <Typography variant="body2" fontWeight="medium">
                {formatCurrency(row.totalCost)}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t('stock.productRecipesPage.cost.perUnit', {
                value: formatCurrency(row.unitCost, 4),
                unit: row.outputUnit,
              })}
            </Typography>
          </>
        );
      },
    },
    {
      id: 'allowNegativeStock',
      label: t('stock.productRecipesPage.columns.negativeStock'),
      align: 'center',
      width: '120px',
      render: (row) => (
        <Chip
          size="small"
          variant="outlined"
          color={row.allowNegativeStock ? 'warning' : 'default'}
          label={
            row.allowNegativeStock
              ? t('stock.productRecipesPage.negativeStock.allowed')
              : t('stock.productRecipesPage.negativeStock.notAllowed')
          }
        />
      ),
    },
    {
      id: 'isActive',
      label: t('stock.productRecipesPage.columns.status'),
      align: 'center',
      width: '100px',
      render: (row) => (
        <Chip
          size="small"
          color={row.isActive ? 'success' : 'default'}
          label={
            row.isActive
              ? t('stock.productRecipesPage.status.active')
              : t('stock.productRecipesPage.status.inactive')
          }
        />
      ),
    },
    {
      id: 'createdAt',
      label: t('stock.productRecipesPage.columns.createdAt'),
      field: 'createdAt',
      sortable: true,
      width: '150px',
      render: (row) => <Typography variant="body2">{formatDate(row.createdAt)}</Typography>,
    },
    {
      id: 'actions',
      label: t('stock.productRecipesPage.columns.actions'),
      align: 'center',
      width: '90px',
      render: (row) => (
        <Tooltip title={t('stock.productRecipesPage.actions.edit')}>
          <span>
            <IconButton
              size="small"
              color="primary"
              disabled={openingRecipeId === row.id}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenRecipe(row);
              }}
            >
              {openingRecipeId === row.id ? <CircularProgress size={18} /> : <EditIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, locale, openingRecipeId, costBranch]);

  const hasFilter = debouncedSearchTerm.trim().length > 0 || statusFilter !== 'all';

  // Arama alanı + durum filtresi (masaüstü ve mobilde aynı bileşenler)
  const searchField = (
    <TextField
      fullWidth
      size="small"
      placeholder={t('stock.productRecipesPage.searchPlaceholder')}
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
      }}
    />
  );

  const branchSelect = (
    <Autocomplete
      fullWidth
      size="small"
      options={branches || []}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={costBranch}
      onChange={(_, value) => setCostBranch(value ? { id: value.id, name: value.name } : null)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('stock.productRecipesPage.branch.label')}
          placeholder={t('stock.productRecipesPage.branch.companyWide')}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <StoreIcon color="primary" fontSize="small" />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  const statusSelect = (
    <FormControl fullWidth size="small">
      <InputLabel>{t('stock.productRecipesPage.filters.status')}</InputLabel>
      <Select
        value={statusFilter}
        label={t('stock.productRecipesPage.filters.status')}
        onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
      >
        <MenuItem value="all">{t('stock.productRecipesPage.filters.all')}</MenuItem>
        <MenuItem value="active">{t('stock.productRecipesPage.status.active')}</MenuItem>
        <MenuItem value="inactive">{t('stock.productRecipesPage.status.inactive')}</MenuItem>
      </Select>
    </FormControl>
  );

  return (
    <>
      {/* Üst Başlık — stok yönetimi ekranıyla aynı düzen */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <RestaurantMenuIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Box>
            <Typography variant={isMobile ? 'h6' : 'h5'} component="h1" fontWeight="bold">
              {t('stock.productRecipesPage.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('stock.productRecipesPage.subtitle')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* İstatistik Kartları */}
      <StatsCardsContainer loading={summaryLoading && !summary}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<RestaurantMenuIcon />}
            color={STATS_COLORS.green}
            title={`${summary?.totalCount ?? 0}`}
            subtitle={t('stock.productRecipesPage.stats.total')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CheckCircleIcon />}
            color={STATS_COLORS.blue}
            title={`${summary?.activeCount ?? 0}`}
            subtitle={t('stock.productRecipesPage.stats.active')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<BlockIcon />}
            color={(summary?.inactiveCount ?? 0) > 0 ? STATS_COLORS.red : STATS_COLORS.grey}
            title={`${summary?.inactiveCount ?? 0}`}
            subtitle={t('stock.productRecipesPage.stats.inactive')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CategoryIcon />}
            color={STATS_COLORS.purple}
            title={`${summary?.ingredientCount ?? 0}`}
            subtitle={t('stock.productRecipesPage.stats.ingredients')}
          />
        </Grid>
      </StatsCardsContainer>

      {/* Arama + filtre + aksiyonlar */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            {searchField}
          </Grid>
          <Grid item xs={12} md={3}>
            {branchSelect}
          </Grid>
          <Grid item xs={12} md={2}>
            {statusSelect}
          </Grid>

          {/* Masaüstü butonlar */}
          <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            <ButtonGroup>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/stok')}>
                {t('stock.productRecipesPage.actions.back')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportToExcel}
                disabled={recipes.length === 0}
              >
                {t('stock.productRecipesPage.actions.downloadExcel')}
              </Button>
              <Tooltip title={t('stock.productRecipesPage.actions.refresh')}>
                <Button variant="outlined" onClick={handleRefresh} disabled={loading} sx={{ minWidth: 'auto', px: 1 }}>
                  <RefreshIcon />
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Grid>

          {/* Mobil butonlar */}
          <Grid item xs={12} sx={{ display: { xs: 'grid', md: 'none' } }}>
            <Grid container spacing={1}>
              <Grid item xs={5}>
                <Button fullWidth size="small" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/stok')}>
                  {t('stock.productRecipesPage.actions.back')}
                </Button>
              </Grid>
              <Grid item xs={5}>
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportToExcel}
                  disabled={recipes.length === 0}
                >
                  {t('stock.productRecipesPage.actions.downloadExcel')}
                </Button>
              </Grid>
              <Grid item xs={2}>
                <Button fullWidth size="small" variant="outlined" onClick={handleRefresh} disabled={loading} sx={{ minWidth: 'auto', px: 0.5 }}>
                  <RefreshIcon fontSize="small" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Aktif filtre bilgisi */}
        {hasFilter && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
            {debouncedSearchTerm.trim() && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${t('stock.productRecipesPage.filters.search')}: ${debouncedSearchTerm}`}
                onDelete={() => setSearchTerm('')}
              />
            )}
            {statusFilter !== 'all' && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={
                  statusFilter === 'active'
                    ? t('stock.productRecipesPage.status.active')
                    : t('stock.productRecipesPage.status.inactive')
                }
                onDelete={() => setStatusFilter('all')}
              />
            )}
            <Button
              size="small"
              color="error"
              variant="text"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
              }}
            >
              {t('stock.productRecipesPage.actions.clearFilters')}
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <InventoryStyleListTable<ProductRecipeListItem>
        columns={columns}
        rows={recipes}
        getRowId={(row) => row.id}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        onSort={(field, direction) => {
          setSortColumn(field);
          setSortDirection(direction);
        }}
        sortField={sortColumn}
        sortDirection={sortDirection}
        clickable
        onRowClick={(row) => handleOpenRecipe(row)}
        emptyMessage={
          hasFilter
            ? t('stock.productRecipesPage.emptyFiltered')
            : t('stock.productRecipesPage.empty')
        }
        ariaLabel={t('stock.productRecipesPage.title')}
        columnResizeStorageKey="product-recipes"
        toolbarInfo={t('stock.productRecipesPage.recordCount', { count: totalCount })}
      />

      <RecipeModal
        open={Boolean(modalItem)}
        onClose={() => setModalItem(null)}
        item={modalItem}
        inventoryItems={[]}
        companyId={companyId || ''}
        warehouses={warehouses}
        categories={categories}
        initialCostBranchId={costBranch?.id ?? null}
        onSuccess={() => {
          fetchRecipes();
          fetchSummary();
        }}
      />
    </>
  );
};

export default ProductRecipes;
